import {
  createDefaultMatterFileRepositoryStructureV1,
  deleteDefaultMatterFileRepositoryStructureV1,
  updateDefaultMatterFileRepositoryStructureV1,
} from "@/lib/data/requests";
import { type MatterSettings } from "@/lib/data/requests/matters/matter-settings";
import { queryKeys } from "@/lib/data/static";
import { type APIError, type APIResponse } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { orderBy } from "lodash";
import toast from "react-hot-toast";

export const useCreateDefaultMatterFileRepositoryStructure = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof createDefaultMatterFileRepositoryStructureV1>[0],
    ) => createDefaultMatterFileRepositoryStructureV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              defaultFileRepositoryStructures: orderBy(
                [
                  ...oldData.data.defaultFileRepositoryStructures,
                  { ...data, id: "temp" },
                ],
                ["label"],
                ["asc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toast.error("Error creating default matter file repository structure");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success(
        "Default matter file repository structure created successfully",
      );
    },
  });
};

export const useUpdateDefaultMatterFileRepositoryStructure = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof updateDefaultMatterFileRepositoryStructureV1>[0],
    ) => updateDefaultMatterFileRepositoryStructureV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(
        error,
        "Error updating default matter file repository structure",
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success(
        "Default matter file repository structure updated successfully",
      );
    },
  });
};

export const useDeleteDefaultMatterFileRepositoryStructure = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string }) =>
      deleteDefaultMatterFileRepositoryStructureV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(
        error,
        "Error deleting default matter file repository structure",
      );
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success(
        "Default matter file repository structure deleted successfully",
      );
    },
  });
};
